/**
 * pricingStrategyMocks.js
 *
 * Simula ÚNICAMENTE la capa de guardarailes, que el backend todavía no expone.
 * No sustituye ningún dato real: todo lo demás (estrategias, reglas, tipos,
 * plantillas, slots de anticipación…) sigue viniendo del API, y los mocks se
 * limitan a enriquecer esas respuestas con lo que faltaría.
 *
 * Con USE_MOCKS = true:
 *   /get-strategy-config  → se le añade la sección `guardrail`
 *   /get-rule-types       → se le añaden los tipos guardrail-*
 *   /get-strategy         → se le añade strategyGuardrails
 *   /get-guardrails, /get-guardrail-templates → servidos de aquí
 *   /set-rule, /delete-rule y los *-strategy-guardrail → simulados en memoria
 *     cuando se trata de un guardarail; las reglas normales van al API real.
 *
 * El estado vive en memoria: se pierde al recargar. Poner a false para
 * trabajar contra backend puro.
 */

export const USE_MOCKS = false;

// Ids de las entidades creadas durante la sesión de pruebas
let _nextId = 9000;

// ── Config de guardarailes que añadiría /get-strategy-config ────────────────

const guardrailConfig = {
  action: {
    modes: ["set_value", "market", "notify"],
    default_mode: "set_value",
    set_value: { min: 0, max: 9999, step: 1, currency: "EUR" },
    notify: { priorities: [1, 2, 3], default_priority: 2 }
  },
  // Sin max_routes ni max_conditions: un guardarail no tiene tope. Se pueden
  // mandar desde backend si algún día hace falta acotarlo.
  max_guardrails: 10
};

// ── Tipos de condición que añadiría /get-rule-types ─────────────────────────
//
// Son condiciones NORMALES: se pueden usar también en reglas. Lo que marca
// `allowed_in_guardrails` es dónde se admiten, porque no tiene sentido que
// todas valgan dentro de un guardarail.
//
// Ojo con el orden de los enum: defaultSettings() elige in[1] || in[0], así
// que el valor por defecto deseado va en segunda posición.

const guardrailRuleTypes = [
  {
    code: "price-lower-than",
    name: "El precio calculado es menor que",
    icon: "mdi-arrow-down-thin",
    group: "price",
    group_icon: "mdi-currency-eur",
    allowed_in_guardrails: true,
    action_modes: ["set_value", "market", "notify"],
    settings_schema: {
      price: ["required", "numeric", "currency", { min: 0, max: 9999 }]
    }
  },
  {
    code: "price-greater-than",
    name: "El precio calculado es mayor que",
    icon: "mdi-arrow-up-thin",
    group: "price",
    group_icon: "mdi-currency-eur",
    allowed_in_guardrails: true,
    action_modes: ["set_value", "market", "notify"],
    settings_schema: {
      price: ["required", "numeric", "currency", { min: 0, max: 9999 }]
    }
  },
  {
    code: "compset-range",
    name: "Estamos en un rango concreto del compset",
    icon: "mdi-storefront-outline",
    group: "compset",
    group_icon: "mdi-storefront-outline",
    allowed_in_guardrails: true,
    action_modes: ["market", "set_value", "notify"],
    settings_schema: {
      // El campo se llama condition_* y no compset_range a propósito: en
      // occupancy-compset ese nombre designa la ACCIÓN (dónde colocarse), y
      // actionMode() infiere "market" en cuanto lo ve dentro de un schema.
      // Aquí es una condición, así que no debe compartir nombre.
      condition_compset_range: [
        "required",
        { in: ["lowest", "low", "middle", "high", "highest"] }
      ]
    }
  },
  {
    code: "occupancy-range",
    name: "Estamos en un rango de ocupación",
    icon: "mdi-chart-line",
    group: "occupancy",
    group_icon: "mdi-chart-line",
    allowed_in_guardrails: true,
    action_modes: ["set_value", "market", "notify"],
    settings_schema: {
      condition_occupancy_from: ["required", "numeric", { min: 0, max: 100 }],
      condition_occupancy_to: ["required", "numeric", { min: 0, max: 100 }]
    }
  }
];

// ── Fixture: guardarailes (get-guardrails) ──────────────────────────────────
//
// Misma forma que una regla, con dos matices:
//  - schedule.frequency = "on_change": no tienen horario propio, se evalúan
//    tras cada cálculo de precio. Se emite un valor en vez de null para que
//    el grafo del nodo trigger funcione sin cambios.
//  - la acción lleva el MODO en la regla y el VALOR en cada ruta.

let mockGuardrails = [
  {
    id: 70,
    hotel_id: 2,
    rate_plan_id: null,
    rule_kind: "guardrail",
    name: "Suelo y techo",
    tag_codes: [],
    color: "#D8E3E5",
    room_type_ids: [],
    schedule: { frequency: "on_change" },
    action: { mode: "set_value" },
    routes: [
      {
        conditions: [
          { rule_type_code: "price-lower-than", settings: { price: 74 } }
        ],
        action: { value: 74, unit: "fixed" }
      },
      {
        conditions: [
          { rule_type_code: "price-greater-than", settings: { price: 1499 } }
        ],
        action: { value: 1499, unit: "fixed" }
      }
    ],
    resume: {
      type: "guardrail",
      condition: "Precio < 74€ · Precio > 1499€",
      actions: ["74€", "1499€"],
      action: "74€ / 1499€"
    },
    strategies_count: 1
  },
  {
    id: 71,
    hotel_id: 2,
    rate_plan_id: null,
    rule_kind: "guardrail",
    name: "No quedarse en lo más caro",
    tag_codes: [],
    color: "#E6E0F7",
    room_type_ids: [],
    schedule: { frequency: "on_change" },
    action: { mode: "market" },
    routes: [
      {
        conditions: [
          {
            rule_type_code: "compset-range",
            settings: { condition_compset_range: "highest" }
          }
        ],
        action: { compset_range: "high" }
      }
    ],
    resume: {
      type: "guardrail",
      condition: "Compset: el más caro",
      actions: ["high"],
      action: "high"
    },
    strategies_count: 1
  },
  {
    id: 72,
    hotel_id: 2,
    rate_plan_id: null,
    rule_kind: "guardrail",
    name: "Avisar si vamos muy vacíos",
    tag_codes: [],
    color: "#F7E5D8",
    room_type_ids: [],
    schedule: { frequency: "on_change" },
    action: { mode: "notify", priority: 3 },
    routes: [
      {
        conditions: [
          {
            rule_type_code: "occupancy-range",
            settings: {
              condition_occupancy_from: 0,
              condition_occupancy_to: 30
            }
          }
        ]
      }
    ],
    resume: {
      type: "guardrail",
      condition: "Ocupación 0-30%",
      actions: ["Avisar"],
      action: "Avisar"
    },
    strategies_count: 0
  },
  // Valor por ruta en modo mercado, con dos rutas (el 71 solo tiene una)
  {
    id: 74,
    hotel_id: 2,
    rate_plan_id: null,
    rule_kind: "guardrail",
    name: "Recolocarse según el compset",
    tag_codes: [],
    color: "#E0EEF7",
    room_type_ids: [],
    schedule: { frequency: "on_change" },
    action: { mode: "market" },
    routes: [
      {
        conditions: [
          {
            rule_type_code: "compset-range",
            settings: { condition_compset_range: "lowest" }
          }
        ],
        action: { compset_range: "low" }
      },
      {
        conditions: [
          {
            rule_type_code: "compset-range",
            settings: { condition_compset_range: "highest" }
          }
        ],
        action: { compset_range: "high" }
      }
    ],
    resume: {
      type: "guardrail",
      condition: "Compset: el más barato o el más caro",
      actions: ["low", "high"],
      action: "low / high"
    },
    strategies_count: 0
  }
];

// ── Fixture: plantillas de guardarail (get-guardrail-templates) ─────────────

const guardrailTemplates = [
  {
    code: "guardrail-compset-reposition",
    name: "Cambiar de rango del compset",
    description:
      "Si el precio calculado nos deja en un rango del compset, muévenos a otro.",
    tag_codes: [],
    rule: {
      rule_kind: "guardrail",
      schedule: { frequency: "on_change" },
      action: { mode: "market" },
      routes: [
        {
          conditions: [
            {
              rule_type_code: "compset-range",
              settings: { condition_compset_range: "highest" }
            }
          ],
          action: { compset_range: "high" }
        }
      ]
    }
  },
  {
    code: "guardrail-occupancy-set-price",
    name: "Fijar precio por ocupación",
    description:
      "Si la ocupación está dentro de un rango, fija un precio concreto.",
    tag_codes: [],
    rule: {
      rule_kind: "guardrail",
      schedule: { frequency: "on_change" },
      action: { mode: "set_value" },
      routes: [
        {
          conditions: [
            {
              rule_type_code: "occupancy-range",
              settings: {
                condition_occupancy_from: 0,
                condition_occupancy_to: 40
              }
            }
          ],
          action: { value: 90, unit: "fixed" }
        }
      ]
    }
  }
];

// ── Estado en memoria de la sesión de pruebas ───────────────────────────────

// Guardarailes enganchados a cada estrategia: { [strategy_id]: [{id, position, rule_id}] }
const strategyGuardrails = {};
let _nextStrategyGuardrailId = 900;

function guardrailsOf(strategyId) {
  if (!strategyGuardrails[strategyId]) strategyGuardrails[strategyId] = [];
  return strategyGuardrails[strategyId];
}

// Hidrata las filas de una estrategia con la definición completa del guardarail
function hydrate(strategyId) {
  return guardrailsOf(strategyId)
    .slice()
    .sort((a, b) => a.position - b.position)
    .map(sg => ({
      id: sg.id,
      position: sg.position,
      rule_id: sg.rule_id,
      locked: false,
      rule: mockGuardrails.find(r => r.id === sg.rule_id)
    }))
    .filter(sg => sg.rule);
}

function delay(ms = 300) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Guardarail fijo de suelo y techo. Lo creará el backend junto con la
// estrategia; aquí se simula con unos valores de ejemplo.
// locked: no se puede borrar ni reordenar, pero sí editar.
const LOCKED_ID = 999;
const LOCKED_MIN = 74;
const LOCKED_MAX = 1499;

function lockedGuardrail() {
  const min = LOCKED_MIN;
  const max = LOCKED_MAX;
  return {
    id: LOCKED_ID,
    position: 999,
    rule_id: LOCKED_ID,
    locked: true,
    rule: {
      id: LOCKED_ID,
      rule_kind: "guardrail",
      name: "Precio mínimo y máximo",
      tag_codes: [],
      color: "#E0E5EA",
      room_type_ids: [],
      schedule: { frequency: "on_change" },
      action: { mode: "set_value" },
      routes: [
        {
          conditions: [
            { rule_type_code: "price-lower-than", settings: { price: min } }
          ],
          action: { value: min, unit: "fixed" }
        },
        {
          conditions: [
            { rule_type_code: "price-greater-than", settings: { price: max } }
          ],
          action: { value: max, unit: "fixed" }
        }
      ],
      resume: {
        type: "guardrail",
        condition: `Precio < ${min}€ · Precio > ${max}€`,
        actions: [`${min}€`, `${max}€`],
        action: `${min}€ / ${max}€`
      },
      strategies_count: 1
    }
  };
}

// ── Enriquecedores de respuestas reales ─────────────────────────────────────
// Reciben la respuesta del API y le añaden lo que el backend aún no devuelve,
// de modo que ningún componente necesita saber que hay mocks por medio.

export function withGuardrailConfig(response) {
  return {
    ...response,
    data: { ...response.data, guardrail: guardrailConfig }
  };
}

export function withGuardrailRuleTypes(response) {
  const real = Array.isArray(response.data) ? response.data : [];
  // Los mocks PISAN al tipo real con el mismo código en vez de añadirse
  // detrás: typeDef() resuelve por el primero que encuentra, así que un
  // duplicado dejaba el mock inservible sin decir nada.
  const codes = guardrailRuleTypes.map(t => t.code);
  return {
    ...response,
    data: [
      ...real.filter(t => codes.indexOf(t.code) === -1),
      ...guardrailRuleTypes
    ]
  };
}

// Si la estrategia ya trae el fijo (porque viene de una pasada anterior), se
// conserva con las ediciones que tuviera.
export function withStrategyGuardrails(response) {
  const strategy = response.data || {};
  const rows = hydrate(strategy.id);
  const previous = (strategy.strategyGuardrails || []).find(g => g.locked);
  const locked = previous || lockedGuardrail();
  return {
    ...response,
    data: {
      ...strategy,
      strategyGuardrails: locked ? [...rows, locked] : rows
    }
  };
}

// ── Lecturas propias ────────────────────────────────────────────────────────

// GET /get-guardrails — biblioteca de guardarailes del hotel
export async function mockGetGuardrails() {
  await delay();
  // Copia: si el state referencia la fixture, un alta la duplicaría
  return { data: [...mockGuardrails] };
}

// GET /get-guardrail-templates — catálogo de guardarailes de ejemplo
export async function mockGetGuardrailTemplates() {
  await delay();
  // Los guardarailes no usan tags
  return { data: { tags: [], templates: guardrailTemplates } };
}

// ── Escrituras simuladas ────────────────────────────────────────────────────

// POST /set-guardrail
export async function mockSetGuardrail(params) {
  await delay();
  if (params.rule_id) {
    const idx = mockGuardrails.findIndex(r => r.id === params.rule_id);
    if (idx === -1) return { error: "Guardrail not found" };
    mockGuardrails[idx] = {
      ...mockGuardrails[idx],
      ...params,
      id: params.rule_id
    };
    return { data: mockGuardrails[idx] };
  }
  const created = { ...params, id: ++_nextId, strategies_count: 0 };
  mockGuardrails.push(created);
  return { data: created };
}

// POST /delete-guardrail
export async function mockDeleteGuardrail(params) {
  await delay();
  mockGuardrails = mockGuardrails.filter(r => r.id !== params.rule_id);
  Object.keys(strategyGuardrails).forEach(id => {
    strategyGuardrails[id] = strategyGuardrails[id].filter(
      sg => sg.rule_id !== params.rule_id
    );
  });
  return { data: { success: true } };
}

// POST /duplicate-guardrail
export async function mockDuplicateGuardrail(params) {
  await delay();
  const original = mockGuardrails.find(r => r.id === params.rule_id);
  if (!original) return { error: "Guardrail not found" };
  const copy = {
    ...JSON.parse(JSON.stringify(original)),
    id: ++_nextId,
    name: original.name + " (copia)",
    strategies_count: 0
  };
  mockGuardrails.push(copy);
  return { data: copy };
}

// Las tres siguientes devuelven la estrategia completa, igual que el endpoint
// real de reglas. Como la estrategia es del API, se recibe y solo se le
// sustituye strategyGuardrails.
export async function mockSetStrategyGuardrail(params, realStrategy) {
  await delay();
  const rows = guardrailsOf(params.strategy_id);
  const existing = params.strategy_guardrail_id
    ? rows.find(sg => sg.id === params.strategy_guardrail_id)
    : null;
  if (existing) {
    existing.rule_id = params.rule_id;
    if (params.position != null) existing.position = params.position;
  } else {
    rows.push({
      id: ++_nextStrategyGuardrailId,
      position: params.position != null ? params.position : rows.length,
      rule_id: params.rule_id
    });
  }
  return withStrategyGuardrails({ data: realStrategy });
}

export async function mockDeleteStrategyGuardrail(params, realStrategy) {
  await delay();
  strategyGuardrails[params.strategy_id] = guardrailsOf(params.strategy_id)
    .filter(sg => sg.id !== params.strategy_guardrail_id)
    .map((sg, i) => ({ ...sg, position: i }));
  return withStrategyGuardrails({ data: realStrategy });
}

export async function mockSortStrategyGuardrails(params, realStrategy) {
  await delay();
  const order = params.guardrail_ids || [];
  strategyGuardrails[params.strategy_id] = guardrailsOf(params.strategy_id)
    .slice()
    .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    .map((sg, i) => ({ ...sg, position: i }));
  return withStrategyGuardrails({ data: realStrategy });
}
